import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { invoiceService, masterDataService } from '../services/api';
import './InvoiceDetail.css';

function InvoiceDetail({ user }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(false);
  const [editedData, setEditedData] = useState({});
  const [validating, setValidating] = useState(false);
  const [validationResult, setValidationResult] = useState(null);
  const [itemsJson, setItemsJson] = useState('[]');
  const [additionalFieldsJson, setAdditionalFieldsJson] = useState('{}');

  // Company CUIT selector states
  const [companies, setCompanies] = useState([]);
  const [selectedCuit, setSelectedCuit] = useState(user?.companyCuit || '');
  const [loadingCuits, setLoadingCuits] = useState(false);

  useEffect(() => {
    loadInvoice();
  }, [id]);

  useEffect(() => {
    // Load company CUITs from MasterDB
    const loadCuits = async () => {
      try {
        setLoadingCuits(true);
        const data = await masterDataService.listCompanyCuits();
        setCompanies(data.companies || []);
        if (!selectedCuit && data.defaultCuit) {
          setSelectedCuit(data.defaultCuit);
        }
      } catch (err) {
        console.error('Error loading company CUITs:', err);
      } finally {
        setLoadingCuits(false);
      }
    };
    loadCuits();
  }, []);

  const loadInvoice = async () => {
    try {
      const response = await invoiceService.getInvoice(id);
      setInvoice(response.invoice);
      setEditedData(response.invoice);
      setItemsJson(JSON.stringify(response.invoice.items || [], null, 2));
      setAdditionalFieldsJson(JSON.stringify(response.invoice.additionalFields || {}, null, 2));
      if (response.invoice?.companyCuit) {
        setSelectedCuit(response.invoice.companyCuit);
      }
    } catch (err) {
      setError('Error al cargar la factura');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    setItemsJson(JSON.stringify(invoice.items || [], null, 2));
    setAdditionalFieldsJson(JSON.stringify(invoice.additionalFields || {}, null, 2));
    setEditing(true);
  };

  const handleCancel = () => {
    setEditing(false);
    setEditedData(invoice);
    setItemsJson(JSON.stringify(invoice.items || [], null, 2));
    setAdditionalFieldsJson(JSON.stringify(invoice.additionalFields || {}, null, 2));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setEditedData({
      ...editedData,
      [name]: value
    });
  };

  const handleSave = async () => {
    try {
      setError('');
      const payload = {
        ...editedData,
        items: itemsJson.trim() ? JSON.parse(itemsJson) : [],
        additionalFields: additionalFieldsJson.trim() ? JSON.parse(additionalFieldsJson) : {}
      };
      await invoiceService.updateInvoice(id, payload);
      await loadInvoice();
      setEditing(false);
    } catch (err) {
      if (err instanceof SyntaxError) {
        setError('Items o Campos adicionales no tienen JSON válido');
      } else {
        setError(err.response?.data?.error || 'Error al actualizar la factura');
      }
      console.error(err);
    }
  };

  const handleSubmitForApproval = async () => {
    try {
      setError('');
      await invoiceService.updateInvoice(id, { ...editedData, status: 'pending_approval' });
      await loadInvoice();
    } catch (err) {
      setError(err.response?.data?.error || 'Error al enviar para aprobación');
      console.error(err);
    }
  };

  const handleValidateAfip = async () => {
    try {
      setError('');
      setValidating(true);
      setValidationResult(null);

      const result = await invoiceService.validateAfip(id, selectedCuit);
      setValidationResult(result);
      await loadInvoice();
    } catch (err) {
      const apiError = err.response?.data || {};
      if (apiError.errorCode === 'MISSING_COMPANY_CUIT') {
        setError('No se puede validar con AFIP: seleccioná una empresa receptora o configurá un CUIT por defecto.');
      } else if (apiError.missingFields?.length > 0) {
        setError(`${apiError.error || 'Error al validar con AFIP'}. Campos faltantes: ${apiError.missingFields.join(', ')}`);
      } else if (apiError.details) {
        setError(`${apiError.error || 'Error al validar con AFIP'}. ${apiError.details}`);
      } else {
        setError(apiError.error || 'Error al validar con AFIP');
      }
      console.error(err);
    } finally {
      setValidating(false);
    }
  };

  const formatDate = (timestamp) => {
    return new Date(timestamp * 1000).toLocaleString('es-AR');
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS'
    }).format(amount);
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      sin_validar: { label: 'Sin Validar AFIP', class: 'badge-sin-validar' },
      Pendiente: { label: 'Pendiente de Aprobación', class: 'badge-pending' },
      draft: { label: 'Borrador', class: 'badge-draft' },
      pending_approval: { label: 'Pendiente de Aprobación', class: 'badge-pending' },
      approved: { label: 'Aprobada', class: 'badge-approved' },
      rejected: { label: 'Rechazada', class: 'badge-rejected' },
      posted: { label: 'Enviada al ERP', class: 'badge-posted' },
      failed: { label: 'Error en Envío', class: 'badge-rejected' }
    };
    
    const statusInfo = statusMap[status] || { label: status, class: 'badge-draft' };
    return <span className={`badge ${statusInfo.class}`}>{statusInfo.label}</span>;
  };

  if (loading) {
    return <div className="container loading">Cargando factura...</div>;
  }

  if (!invoice) {
    return (
      <div className="container">
        <div className="alert alert-error">Factura no encontrada</div>
      </div>
    );
  }

  const canEdit = (invoice.status === 'draft' || invoice.status === 'rejected' || invoice.status === 'sin_validar') && user?.canEditInvoices;
  const canSubmit = invoice.status === 'draft' && user?.canEditInvoices;
  const canValidateAfip = invoice.status === 'sin_validar' && user?.canEditInvoices;

  return (
    <div className="container">
      <div className="page-header">
        <div>
          <button onClick={() => navigate('/invoices')} className="btn btn-secondary">
            ← Volver
          </button>
          <h1 style={{ marginTop: '10px' }}>Detalle de Factura</h1>
        </div>
        <div>
          {getStatusBadge(invoice.status)}
        </div>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {invoice.status === 'rejected' && invoice.rejectionComments && (
        <div className="alert alert-error">
          <strong>Motivo del rechazo:</strong> {invoice.rejectionComments}
        </div>
      )}

      <div className="card">
        <h2>Identificación</h2>
        {editing ? (
          <div className="form-row">
            <div className="form-group">
              <label>Tipo de Comprobante</label>
              <input
                type="text"
                name="tipoComprobante"
                className="form-control"
                value={editedData.tipoComprobante || ''}
                onChange={handleChange}
              />
            </div>
            <div className="form-group">
              <label>Punto de Venta</label>
              <input
                type="text"
                name="puntoVenta"
                className="form-control"
                value={editedData.puntoVenta || ''}
                onChange={handleChange}
              />
            </div>
            <div className="form-group">
              <label>Número</label>
              <input
                type="text"
                name="invoiceNumber"
                className="form-control"
                value={editedData.invoiceNumber || ''}
                onChange={handleChange}
              />
            </div>
          </div>
        ) : (
          <div className="detail-grid">
            <div className="detail-item">
              <span className="detail-label">Tipo:</span>
              <span className="detail-value">{invoice.tipoComprobante}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Punto de Venta:</span>
              <span className="detail-value">{invoice.puntoVenta}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Número:</span>
              <span className="detail-value">{invoice.invoiceNumber}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Nº Completo:</span>
              <span className="detail-value">
                {invoice.tipoComprobante}-{invoice.puntoVenta}-{invoice.invoiceNumber}
              </span>
            </div>
          </div>
        )}
      </div>

      <div className="card">
        <h2>Proveedor</h2>
        {editing ? (
          <div className="form-row">
            <div className="form-group">
              <label>CUIT</label>
              <input
                type="text"
                name="supplierCuit"
                className="form-control"
                value={editedData.supplierCuit || ''}
                onChange={handleChange}
              />
            </div>
            <div className="form-group">
              <label>Nombre</label>
              <input
                type="text"
                name="supplierName"
                className="form-control"
                value={editedData.supplierName || ''}
                onChange={handleChange}
              />
            </div>
          </div>
        ) : (
          <div className="detail-grid">
            <div className="detail-item">
              <span className="detail-label">CUIT:</span>
              <span className="detail-value">{invoice.supplierCuit || 'No especificado'}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Nombre:</span>
              <span className="detail-value">{invoice.supplierName || 'No especificado'}</span>
            </div>
          </div>
        )}
      </div>

      <div className="card">
        <h2>Detalles de Factura</h2>
        {editing ? (
          <div className="form-row">
            <div className="form-group">
              <label>Fecha</label>
              <input
                type="date"
                name="invoiceDate"
                className="form-control"
                value={editedData.invoiceDate || ''}
                onChange={handleChange}
              />
            </div>
            <div className="form-group">
              <label>Moneda</label>
              <input
                type="text"
                name="currency"
                className="form-control"
                value={editedData.currency || 'ARS'}
                onChange={handleChange}
              />
            </div>
            <div className="form-group">
              <label>Monto Total</label>
              <input
                type="number"
                name="totalAmount"
                className="form-control"
                value={editedData.totalAmount || 0}
                onChange={handleChange}
                step="0.01"
              />
            </div>
            <div className="form-group">
              <label>CAE (Código de Autorización Electrónica)</label>
              <input
                type="text"
                name="cae"
                className="form-control"
                value={editedData.cae || ''}
                onChange={handleChange}
                placeholder="Ej: 12345678901234"
              />
            </div>
            <div className="form-group">
              <label>Empresa receptora (CUIT)</label>
              <select
                name="companyCuit"
                className="form-control"
                value={editedData.companyCuit || ''}
                onChange={handleChange}
              >
                <option value="">-- Seleccionar CUIT --</option>
                {companies.map((company) => (
                  <option key={company.cuit} value={company.cuit}>
                    {company.companyName} ({company.cuit})
                  </option>
                ))}
              </select>
            </div>
          </div>
        ) : (
          <div className="detail-grid">
            <div className="detail-item">
              <span className="detail-label">Fecha:</span>
              <span className="detail-value">{invoice.invoiceDate || 'No especificada'}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Moneda:</span>
              <span className="detail-value">{invoice.currency}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Monto Total:</span>
              <span className="detail-value" style={{ fontSize: '20px', fontWeight: 'bold', color: '#667eea' }}>
                {formatAmount(invoice.totalAmount)}
              </span>
            </div>
            {invoice.cae && (
              <div className="detail-item">
                <span className="detail-label">CAE:</span>
                <span className="detail-value">{invoice.cae}</span>
              </div>
            )}
            <div className="detail-item">
              <span className="detail-label">Empresa receptora (CUIT):</span>
              <span className="detail-value">{invoice.companyCuit || 'Consumidor final'}</span>
            </div>
          </div>
        )}
      </div>

      <div className="card">
        <h2>Items</h2>
        {editing ? (
          <div className="form-group">
            <label>JSON de Items</label>
            <textarea
              className="form-control json-editor"
              rows="10"
              value={itemsJson}
              onChange={(e) => setItemsJson(e.target.value)}
            />
          </div>
        ) : (
          <>
            {invoice.items && invoice.items.length > 0 ? (
              <table className="table">
                <thead>
                  <tr>
                    <th>Descripción</th>
                    <th>Cantidad</th>
                    <th>Precio Unit.</th>
                    <th>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {invoice.items.map((item, index) => (
                    <tr key={index}>
                      <td>{item.description}</td>
                      <td>{item.quantity}</td>
                      <td>{formatAmount(item.unitPrice)}</td>
                      <td>{formatAmount(item.total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="detail-value">Sin items</div>
            )}
          </>
        )}
      </div>

      <div className="card">
        <h2>Campos Adicionales</h2>
        {editing ? (
          <div className="form-group">
            <label>JSON de Campos Adicionales</label>
            <textarea
              className="form-control json-editor"
              rows="8"
              value={additionalFieldsJson}
              onChange={(e) => setAdditionalFieldsJson(e.target.value)}
            />
          </div>
        ) : (
          <pre className="invoice-xml"><code>{JSON.stringify(invoice.additionalFields || {}, null, 2)}</code></pre>
        )}
      </div>

      {/* Tarjeta de Validación AFIP */}
      {(invoice.status === 'sin_validar' || invoice.afipValidationStatus) && (
        <div className="card">
          <h2>Validación AFIP</h2>

          {invoice.afipValidationStatus && (
            <div className="detail-grid" style={{ marginBottom: '15px' }}>
              <div className="detail-item">
                <span className="detail-label">Estado:</span>
                <span className={`badge ${invoice.afipValidationStatus === 'valid' ? 'badge-afip-valid' : 'badge-afip-invalid'}`}>
                  {invoice.afipValidationStatus === 'valid' ? 'Válido en AFIP' : 'Rechazado por AFIP'}
                </span>
              </div>
              {invoice.afipValidationDate && (
                <div className="detail-item">
                  <span className="detail-label">Fecha de validación:</span>
                  <span className="detail-value">{formatDate(invoice.afipValidationDate)}</span>
                </div>
              )}
              {invoice.afipValidationResult?.observaciones?.length > 0 && (
                <div className="detail-item" style={{ gridColumn: '1 / -1' }}>
                  <span className="detail-label">Observaciones AFIP:</span>
                  <span className="detail-value">
                    {invoice.afipValidationResult.observaciones
                      .map(o => typeof o === 'object' ? `[${o.code}] ${o.msg}` : o)
                      .join(' | ')}
                  </span>
                </div>
              )}
              {invoice.afipValidationResult?.errores?.length > 0 && (
                <div className="detail-item" style={{ gridColumn: '1 / -1' }}>
                  <span className="detail-label">Errores AFIP:</span>
                  <span className="detail-value">
                    {invoice.afipValidationResult.errores
                      .map(e => typeof e === 'object' ? `[${e.code}] ${e.msg}` : e)
                      .join(' | ')}
                  </span>
                </div>
              )}
            </div>
          )}

          {validationResult && (
            <div className={`alert ${validationResult.afipValidationStatus === 'valid' ? 'alert-success' : 'alert-error'}`}>
              {validationResult.afipValidationStatus === 'valid'
                ? '✓ Comprobante válido en AFIP. La factura pasó a estado Pendiente de Aprobación.'
                : (() => {
                    const afip = validationResult.afipResult || {};
                    const lines = [`✗ AFIP rechazó el comprobante. Resultado: ${afip.resultado}`];
                    const fmt = (arr) => (arr || []).map(i => `[${i.code}] ${i.msg}`).join(' | ');
                    if (afip.errores?.length)       lines.push(`Errores: ${fmt(afip.errores)}`);
                    if (afip.observaciones?.length) lines.push(`Observaciones: ${fmt(afip.observaciones)}`);
                    if (afip.eventos?.length)       lines.push(`Eventos: ${fmt(afip.eventos)}`);
                    return lines.map((l, i) => <div key={i}>{l}</div>);
                  })()
              }
            </div>
          )}

          {canValidateAfip && !editing && (
            <div>
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                  Empresa autenticadora (certificado AFIP):
                </label>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <select
                    value={selectedCuit}
                    onChange={(e) => setSelectedCuit(e.target.value)}
                    className="form-control"
                    style={{ flex: 1 }}
                    disabled={loadingCuits || validating}
                  >
                    <option value="">-- Seleccionar empresa --</option>
                    {companies.map((company) => (
                      <option key={company.cuit} value={company.cuit}>
                        {company.companyName} ({company.cuit})
                      </option>
                    ))}
                  </select>
                </div>
                <small style={{ color: '#666', marginTop: '4px', display: 'block' }}>
                  Empresa cuyo certificado se usa para consultar en AFIP. El receptor de la factura
                  es <strong>{invoice.companyCuit || 'Consumidor final'}</strong> y se toma del dato guardado en la factura.
                </small>
              </div>

              {!invoice.cae && (
                <div className="alert alert-info" style={{ marginBottom: '12px' }}>
                  La factura no tiene CAE. Editá la factura para ingresarlo antes de validar.
                </div>
              )}
              <button
                onClick={handleValidateAfip}
                className="btn btn-warning"
                disabled={validating || !invoice.cae || !selectedCuit}
              >
                {validating ? 'Validando con AFIP...' : '✔ Validar con AFIP'}
              </button>
            </div>
          )}
        </div>
      )}
      {invoice.approvalHistory && invoice.approvalHistory.length > 0 && (
        <div className="card">
          <h2>Historial de Aprobación</h2>
          <div className="approval-history">
            {invoice.approvalHistory.map((history, index) => (
              <div key={index} className="history-item">
                <div><strong>{history.action === 'approved' ? 'Aprobado' : 'Rechazado'}</strong></div>
                <div>Por: {history.approvedBy || history.rejectedBy}</div>
                <div>Fecha: {formatDate(history.timestamp)}</div>
                {history.comments && <div>Comentarios: {history.comments}</div>}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="card">
        <h2>Información de Creación</h2>
        <div className="detail-grid">
          <div className="detail-item">
            <span className="detail-label">Creado por:</span>
            <span className="detail-value">{invoice.createdBy}</span>
          </div>
          <div className="detail-item">
            <span className="detail-label">Fecha de creación:</span>
            <span className="detail-value">{formatDate(invoice.createdAt)}</span>
          </div>
          <div className="detail-item">
            <span className="detail-label">Última actualización:</span>
            <span className="detail-value">{formatDate(invoice.updatedAt)}</span>
          </div>
        </div>
      </div>

      <div className="form-actions">
        {editing ? (
          <>
            <button onClick={handleCancel} className="btn btn-secondary">
              Cancelar
            </button>
            <button onClick={handleSave} className="btn btn-primary">
              Guardar Cambios
            </button>
          </>
        ) : (
          <>
            {canEdit && (
              <button onClick={handleEdit} className="btn btn-secondary">
                Editar
              </button>
            )}
            {canSubmit && (
              <button onClick={handleSubmitForApproval} className="btn btn-success">
                Enviar para Aprobación
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default InvoiceDetail;
